import { useCallback, useEffect, useState, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import { FieldPanel, FormHeader, LineBreak, tooltipStyles } from '../../TemplateForm/styles';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { selectedWorklogsTemplate, selectedWorklogsTemplateId } from '../../state';
import {
  AllLogsTemplatesDocument,
  CardConfig,
  Equation,
  ExpressionType,
  GetVersionsDocument,
  SortTypeEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TextFormatEnum,
  useUpdateLogsTemplateMutation,
  LogsTemplateDocument,
  LogsTemplateField,
} from '@/__generated__/graphql';
import { keyFromTitle, useSnackBarMessage, cleanEquations, cleanCardConfigs } from '../../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { CardsConfigTab } from './CardsConfigTab';
import { EquationsConfigTab } from './EquationsConfigTab';
import { FloatingLine, workpackTabStyles } from '../styles';

type DerivedFieldsFormProps = {
  structureId: string;
};

export const DerivedFieldsForm = ({ structureId }: DerivedFieldsFormProps) => {
  const worklogsTemplate = useAtomValue(selectedWorklogsTemplate);
  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [cards, setCards] = useState<CardConfig[]>([]);
  const [equations, setEquations] = useState<Equation[]>([]);
  const [originalCards, setOriginalCards] = useState<CardConfig[]>([]);
  const [originalEquations, setOriginalEquations] = useState<Equation[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [updateWorklogsTemplate] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument],
    onCompleted: () => {
      showSnackBar('Worklogs template updated successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const availableEquations = useMemo(() => {
    return equations.map((equation) => ({
      key: equation.key,
      name: equation.name || equation.key,
    }));
  }, [equations]);

  const availableFieldKeys = useMemo(() => {
    return (
      worklogsTemplate?.fields
        ?.map((field) => field.key)
        .filter((key): key is string => Boolean(key)) || []
    );
  }, [worklogsTemplate?.fields]);

  useEffect(() => {
    if (worklogsTemplate) {
      const filteredCards =
        worklogsTemplate?.cardsConfig?.filter((config) => config !== null) || [];
      const filteredEquations =
        worklogsTemplate?.equations?.filter((equation) => equation !== null) || [];

      setCards(filteredCards);
      setEquations(filteredEquations);
      setOriginalCards(filteredCards);
      setOriginalEquations(filteredEquations);
    }
  }, [worklogsTemplate]);

  const handleCardsChange = useCallback((newCards: CardConfig[]) => {
    setCards(newCards);
  }, []);

  const handleEquationsChange = useCallback((newEquations: Equation[]) => {
    setEquations(newEquations);
  }, []);

  const handleCreateNewField = useCallback(
    (fieldKey: string, equationKey: string) => {
      if (!worklogsTemplate?.fields) return;

      // Check if field already exists
      const existingField = worklogsTemplate.fields.find((field) => field.key === fieldKey);
      if (existingField) return;

      // Create new derived field
      const newField: LogsTemplateField = {
        id: `newField_${Date.now()}`,
        key: fieldKey,
        name: fieldKey.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase()),
        type: TemplateFieldTypeEnum.Input,
        options: [],
        isReadOnly: false,
        isRequired: false,
        displayInReports: true,
        displayInReportFilters: false,
        displayInLogAggregation: true,
        isDerived: true,
        derivedEquationKey: equationKey,
        serverSide: false,
        displayConfig: {
          id: `display_${Date.now()}`,
          textFormat: TextFormatEnum.None,
          isSortable: true,
          sortType: SortTypeEnum.Number,
          type: TableCellStyleEnum.Text,
        },
      };

      // Update workpack template fields through the mutation
      const updatedFields = [...worklogsTemplate.fields, newField];

      // Trigger a save to update the template with the new field
      updateWorklogsTemplate({
        variables: {
          input: {
            structureId,
            templateId: worklogsTemplateId!,
            fields: updatedFields,
            lastModifiedFor: 'ADD_DERIVED_FIELD',
          },
        },
      });

      showSnackBar(`Created new derived field: ${newField.name}`, 'success');
    },
    [
      worklogsTemplate?.fields,
      structureId,
      worklogsTemplateId,
      updateWorklogsTemplate,
      showSnackBar,
    ]
  );

  const createFirstCard = useCallback(() => {
    const newCard: CardConfig = {
      id: generateMongoObjectId(),
      key: keyFromTitle('New KPI Card'),
      title: 'New KPI Card',
      order: 1,
      icon: 'unknown',
      equationKey: availableEquations[0]?.key || null,
      aggregation: null,
      subtitleConfig: null,
    };
    setCards([newCard]);
    setMode('Edit');
  }, [availableEquations]);

  const createFirstEquation = useCallback(() => {
    const newEquation: Equation = {
      key: keyFromTitle('New Equation'),
      name: 'New Equation',
      variables: [],
      expression: {
        type: ExpressionType.Number,
        value: 0,
      },
    };
    setEquations([newEquation]);
    setMode('Edit');
  }, []);

  const [saveWorklogsTemplate, { loading: isLoading }] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar('An error occurred while updating worklogs template', 'error');
        return;
      }
      showSnackBar('Worklogs template configurations saved successfully', 'success');
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
      setMode('ReadOnly');
    },
  });

  const handleCancel = useCallback(() => {
    setCards([...originalCards]);
    setEquations([...originalEquations]);
    setMode('ReadOnly');
  }, [originalCards, originalEquations]);

  const handleSaveWorklogsTemplate = useCallback(() => {
    // Clean the data to remove __typename fields and fix enum values
    const cleanedEquations = cleanEquations(equations);
    const cleanedCards = cleanCardConfigs(cards);

    saveWorklogsTemplate({
      variables: {
        input: {
          structureId,
          templateId: worklogsTemplateId ?? '',
          lastModifiedFor: 'Update Card Config',
          equations: cleanedEquations,
          cardsConfig: cleanedCards,
        },
      },
      refetchQueries: [GetVersionsDocument],
    });
  }, [cards, equations, saveWorklogsTemplate, structureId, worklogsTemplateId]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Equations',
        component: (
          <EquationsConfigTab
            mode={mode}
            equations={equations}
            availableFieldKeys={availableFieldKeys}
            availableFields={worklogsTemplate?.fields?.filter((field) => field !== null) || []}
            onEquationsChange={handleEquationsChange}
            onCreateNewField={handleCreateNewField}
            originalEquations={originalEquations}
          />
        ),
      },
      {
        index: 1,
        label: 'Cards Configuration',
        component: (
          <CardsConfigTab
            mode={mode}
            cards={cards}
            availableEquations={availableEquations}
            onCardsChange={handleCardsChange}
            originalCards={originalCards}
          />
        ),
      },
    ];
  }, [
    mode,
    equations,
    availableFieldKeys,
    handleEquationsChange,
    handleCreateNewField,
    originalEquations,
    cards,
    availableEquations,
    handleCardsChange,
    originalCards,
    worklogsTemplate?.fields,
  ]);

  if ((!cards || cards.length === 0) && (!equations || equations.length === 0))
    return (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <Stack spacing={3} alignItems="center">
            <EmptyStatePlaceholder type="template" message="No KPI Configuration Found" />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Get started by creating your first KPI card or equation
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={createFirstCard}
                sx={containedButtonStyle}
              >
                Create First Card
              </Button>
              <Button variant="outlined" startIcon={<Add />} onClick={createFirstEquation}>
                Create First Equation
              </Button>
            </Stack>
          </Stack>
        </FieldPanel>
      </Box>
    );

  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          KPI Derived Fields
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit Structure"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton onClick={() => setMode('Edit')}>
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSaveWorklogsTemplate}
              disabled={isLoading}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        tabsStyles={workpackTabStyles}
        customSeperator={<FloatingLine />}
        renderingStrategy="eager"
      />
    </Stack>
  );
};
